import { BriefcaseIcon, PencilIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Card, CardBody, CardHeader } from '@heroui/react';
import { type FC, useEffect, useState } from 'react';
import { NoData } from '@/app/components/no-data.tsx';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import {
  AccountCategoryActionEnum,
  AccountCategoryFormDialog,
} from '@/app/pages/account/settings/account-category-form-dialog';
import toast from '@/app/toast.ts';
import type { AccountModel } from '@/app/types/account.type.ts';
import type { AccountCategoryModel } from '@/app/types/account-category.type.ts';
import {
  useDeleteAccountCategoryMutation,
  usePostAccountCategoryMutation,
  usePutAccountCategoryMutation,
} from '@/slices/account-api-slice.ts';

export interface AccountCategoriesProps {
  account: AccountModel;
}

export const AccountCategories: FC<AccountCategoriesProps> = ({ ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState(0);
  const [initialValue, setInitialValue] = useState<Partial<AccountCategoryModel>>();
  const [postCategory, { isLoading: postLoading, isSuccess: postSuccess, isError: postError }] =
    usePostAccountCategoryMutation();
  const [putCategory, { isLoading: putLoading, isSuccess: putSuccess, isError: putError }] =
    usePutAccountCategoryMutation();
  const [
    deleteCategory,
    { isLoading: deleteLoading, isSuccess: deleteSuccess, isError: deleteError },
  ] = useDeleteAccountCategoryMutation();

  const handleOnClose = (value: AccountCategoryModel) => {
    if (
      !value.isDefault &&
      (!props.account.categories?.length ||
        props.account.categories.filter(x => x.id !== value.id).every(x => !x.isDefault))
    ) {
      toast.error('At least one category must be set as default.');
      return;
    }

    if (value && action === AccountCategoryActionEnum.add) {
      value.accountId = props.account.id;
      postCategory(value);
    } else if (value && action === AccountCategoryActionEnum.edit) {
      putCategory(value);
    }

    setIsOpen(false);
  };

  const openAddDialog = () => {
    setAction(AccountCategoryActionEnum.add);
    setInitialValue({
      accountId: props.account.id,
      isDefault:
        !props.account.categories?.length || props.account.categories.every(x => !x.isDefault),
      description: '',
      id: 0,
      frequency: null,
    });
    setIsOpen(true);
  };

  const openEditDialog = (value: AccountCategoryModel) => {
    setAction(AccountCategoryActionEnum.edit);
    setInitialValue(value);
    setIsOpen(true);
  };

  const handleDelete = (value: AccountCategoryModel) => {
    deleteCategory({ accountKey: props.account.key, id: value.id });
  };

  useEffect(() => {
    let action = '';
    if (postSuccess || postError) {
      action = 'added';
    } else if (putSuccess || putError) {
      action = 'updated';
    } else if (deleteSuccess || deleteError) {
      action = 'deleted';
    }

    if (postSuccess || putSuccess || deleteSuccess) {
      toast.success(`Rep Category successfully ${action}.`);
    }
    if (postError || putError || deleteError) {
      toast.error(`An error has occurred. Rep Category could not be ${action}.`);
    }
  }, [postSuccess, postError, putSuccess, putError, deleteSuccess, deleteError]);

  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    putLoading || postLoading || deleteLoading
      ? showPageLoading('edit-rep-categories')
      : hidePageLoading('edit-rep-categories');
  }, [putLoading, postLoading, deleteLoading]);

  const repCategories = props.account?.categories ?? [];

  return (
    <>
      <AccountCategoryFormDialog
        action={action}
        initialValue={initialValue}
        onClose={handleOnClose}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      <Card>
        <CardHeader className='flex justify-between items-center px-4 pt-4'>
          <div className='flex items-center gap-2'>
            <BriefcaseIcon className='size-5' aria-hidden='true' />
            <h2 className='font-semibold'>Rep Categories</h2>
            <div className='h-6 w-6 flex items-center justify-center font-semibold rounded-full bg-default-200 text-default-500 text-sm'>
              {repCategories.length}
            </div>
          </div>
          <Button color='primary' variant='bordered' onPress={openAddDialog}>
            <PlusIcon className='size-5' />
            Add Category
          </Button>
        </CardHeader>
        <CardBody className='px-4 pb-4'>
          <div className='flex-col'>
            {!repCategories.length && <NoData message='No rep categories found' />}
            {repCategories.map(category => {
              const isDefault = category.isDefault;
              return (
                <div
                  className='flex items-center px-4 py-2 listGroupItem bg-default-50'
                  key={category.id}
                >
                  <div className='flex-1'>
                    {category.description} {isDefault ? '(Default)' : ''}
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      isIconOnly
                      size='sm'
                      variant='flat'
                      color='primary'
                      onPress={() => openEditDialog(category)}
                    >
                      <PencilIcon className='size-4' />
                    </Button>
                    {!isDefault && (
                      <Button
                        isIconOnly
                        size='sm'
                        color='danger'
                        variant='flat'
                        onPress={() => handleDelete(category)}
                      >
                        <XMarkIcon className='size-4' />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
    </>
  );
};
